import { bool, func, node, string } from "prop-types";
import { forwardRef } from "react";

import styled from "styled-components/macro";

import {
  AnimatePresence,
  motion,
  useMotionValue,
  useTransform,
} from "motion/react";

import useComponentIsMounted from "Utilities/Hooks/useComponentIsMounted";

import useAnimationStyles from "./hooks";

const Component = forwardRef(
  (
    {
      show,
      bottom = false,
      top = !bottom, // This way, if bottom is set to true there is no need to set top to false.
      left = false,
      right = false,
      height,
      width,
      muted,
      setIsAnimating,
      inertWhileAnimating = false,
      children,
      style,
      ...rest
    },
    ref
  ) => {
    const isMounted = useComponentIsMounted();

    const { visible, hidden } = useAnimationStyles({
      top,
      bottom,
      left,
      right,
      height,
      width,
    });

    // Opt-in (`inertWhileAnimating`): a fading/sliding box is never a valid
    // interaction target, so gate pointer-events off until it settles. Every
    // variant animates opacity 0↔1 (see hooks.js), so the live opacity is the
    // signal — no animation-callback race, and a box that mounts already-visible
    // (opacity 1) is interactive from the first frame. Opt-in so existing
    // consumers' rendered output is unchanged. (Fixes the address-callout "hover
    // flickers as the pill slides under the cursor on reveal" bug.)
    const startVisible = show && !isMounted;
    const opacity = useMotionValue(startVisible ? 1 : 0);
    const pointerEvents = useTransform(opacity, (o) =>
      o < 1 ? "none" : "auto"
    );
    const inertStyle = inertWhileAnimating ? { opacity, pointerEvents } : null;

    return (
      <AnimatePresence>
        {show && (
          <motion.div
            ref={ref}
            initial={startVisible ? visible : hidden}
            animate={visible}
            exit={hidden}
            onAnimationStart={() => setIsAnimating(true)}
            onAnimationComplete={() => setIsAnimating(false)}
            {...rest}
            style={{ ...style, ...inertStyle }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }
);

Component.propTypes = {
  show: bool,
  muted: bool,
  height: string,
  width: string,
  top: bool,
  bottom: bool,
  left: bool,
  right: bool,
  children: node,
  setIsAnimating: func,
  inertWhileAnimating: bool,
};

const FnrFade = forwardRef(
  /**
   * A simple disappearing trick.
   * @param {object} param0
   * @param {boolean} param0.show - Should this be displaying?
   * @param {string} param0.height - default to auto; change as needed
   * @param {string} param0.width - default to auto; change as needed
   * @param {boolean} param0.muted - Deactivates effect
   * @param {boolean} param0.top - Causes wrapped component to come in from the top. Defaults to true
   * @param {boolean} param0.bottom - Causes wrapped component to come in from the bottom
   * @param {boolean} param0.left - Causes wrapped component to come in from the left
   * @param {boolean} param0.right - Causes wrapped component to come in from the right
   * @param {boolean} param0.inertWhileAnimating - Disables pointer-events on the content while it animates in/out, so a control inside can't be clicked or flick :hover while the box is still moving. Off by default.
   * @param {import("react").RefObject} ref
   */
  (
    {
      muted,
      show,
      height = "auto",
      width = "auto",
      setIsAnimating = () => {},
      inertWhileAnimating = false,
      ...rest
    },
    ref
  ) => {
    if (muted && !show) {
      return null;
    } else if (muted) {
      return (
        <SDiv
          ref={ref}
          $height={height}
          $width={width}
          data-cy="FnrFade_Muted"
          {...rest}
        />
      );
    }
    return (
      <Component
        ref={ref}
        height={height}
        width={width}
        show={show}
        data-cy="FnrFade"
        setIsAnimating={setIsAnimating}
        inertWhileAnimating={inertWhileAnimating}
        {...rest}
      />
    );
  }
);

const SDiv = styled("div")`
  ${({ $width }) => `width: ${$width};`}
  ${({ $height }) => `height: ${$height};`}
`;

FnrFade.propTypes = {
  show: bool,
  muted: bool,
  height: string,
  width: string,
  top: bool,
  bottom: bool,
  left: bool,
  right: bool,
  setIsAnimating: func,
  inertWhileAnimating: bool,
};

export default FnrFade;
